import pdfMake from 'addthaifont-pdfmake';
import 'addthaifont-pdfmake/build/vfs_fonts';
import dayjs from 'dayjs';
import { computeProjectCostRows } from '../Table/payroll/projectCostFormula';

pdfMake.fonts = {
  Sarabun: {
    normal: 'Sarabun-Light.ttf',
    bold: 'Sarabun-Regular.ttf',
    italics: 'Sarabun-LightItalic.ttf',
    bolditalics: 'Sarabun-Italic.ttf',
  },
};

const fmt = (n) =>
  Number(n || 0).toLocaleString('th-TH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const LEAVE_ABBR = {
  SICK: 'ป่วย',
  PERSONAL: 'กิจ',
  VACATION: 'พักร้อน',
  MATERNITY: 'คลอด',
  ORDINATION: 'บวช',
  UNPAID: 'ไม่จ้าง',
};

// เหมือน DAY_TH_ABBR ใน components/Table/payroll/constants.js (ใช้ร่วมกับหน้าจอ MergedPayrollTableV2)
const DAY_TH_ABBR = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'];

// map: empId → { 'YYYY-MM-DD': leaveRecord } — เหมือนกับที่ timeStampMontlyReport.js ใช้
const buildLeaveMap = (leaveData) => {
  const map = {};
  (leaveData || []).forEach((lr) => {
    const empId = String(lr.employee?._id || lr.employee || '');
    if (!empId) return;
    if (!map[empId]) map[empId] = {};
    let d = dayjs(lr.start_date).startOf('day');
    const lEnd = dayjs(lr.end_date).startOf('day');
    while (!d.isAfter(lEnd)) {
      map[empId][d.format('YYYY-MM-DD')] = lr;
      d = d.add(1, 'day');
    }
  });
  return map;
};

// PDF สรุปทั้งหน้า /humen/hr-report-v2 — ดึงเลขจากที่เดียวกับที่หน้าจอใช้จริง
// (empSummary + liveSalaryData มาจาก MergedPayrollTableV2, projectDashboard มาจาก TimestampProjectCost)
// เพื่อไม่ให้ PDF เพี้ยนไปจากหน้าจอเหมือนตัวพิมพ์เดิมที่ยังอ่าน timestamp ดิบจาก backend
const hrReportFullPDF = ({
  info,
  month,
  year,
  dateStart,
  days = [],
  timestamp,
  empSummary = {},
  liveSalaryData = [],
  leaveData = [],
  projectDashboard = [],
  holidayDaySet = new Set(),
  salaryMode = 'late-deduct',
}) => {
  const salaryByEmpId = {};
  (liveSalaryData || []).forEach((row) => {
    if (row?.employee_id != null) salaryByEmpId[String(row.employee_id)] = row;
  });
  const leaveMap = buildLeaveMap(leaveData);

  const rows = timestamp?.rows || [];

  // dow/isHoliday ต่อวัน — คำนวณครั้งเดียวใช้ร่วมกันทั้ง header และทุกแถวพนักงาน
  // (เหมือน dayMeta ใน MergedPayrollTableV2.js — เสาร์ไม่ทำสีพิเศษ ถือเป็นวันทำงานปกติ
  // เว้นแต่ตรงกับวันหยุดบริษัทจริง (holidayDaySet) ต่างจากเดิมที่ทำสีแดงเสาร์-อาทิตย์เหมือนกันหมด)
  const dayMeta = days.map((day) => ({
    day,
    dow: new Date(year, month, day).getDay(),
    isHoliday: holidayDaySet.has(day),
  }));

  // ── ตาราง 1: ข้อมูลการลงเวลา (วันต่อวัน เหมือนตารางเดิม) ──
  const attendanceBody = [
    [
      { text: 'No.', style: 'tableHeader' },
      { text: 'ชื่อ-สกุล', style: 'tableHeader', alignment: 'left' },
      { text: 'ประเภท', style: 'tableHeader' },
      ...dayMeta.map(({ day, dow, isHoliday }) => ({
        text: [
          { text: `${DAY_TH_ABBR[dow]}\n`, fontSize: 4 },
          { text: day.toString(), fontSize: 6 },
        ],
        style: 'tableHeader',
        fillColor: isHoliday ? '#fecaca' : dow === 0 ? '#fca5a5' : '#e6e6e6',
      })),
      { text: 'Day Rate', style: 'tableHeader' },
      { text: 'วันทำงาน', style: 'tableHeader' },
      { text: 'OT ชม.', style: 'tableHeader' },
    ],
  ];
  rows.forEach((emp, idx) => {
    const empId = String(emp.employee_id || '');
    const summary = empSummary[empId] || {};
    const empLeaves = leaveMap[empId] || {};

    const dayCells = dayMeta.map(({ day }) => {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const foundCk = (emp.checkins || []).find(
        (c) => dayjs(c.checkIn).format('YYYY-MM-DD') === dateStr,
      );
      const leave = empLeaves[dateStr];
      if (!foundCk && leave) {
        const abbr = LEAVE_ABBR[leave.leave_type] || 'ลา';
        return { text: leave.period !== 'FULL_DAY' ? `½${abbr}` : abbr, alignment: 'center', fontSize: 5, color: '#be185d', fillColor: '#fce7f3' };
      }
      if (!foundCk) return { text: '', alignment: 'center' };
      const rate = foundCk.rate || 1;
      const h = (foundCk.totalHour || 0) * rate;
      return {
        text: rate > 1
          ? [{ text: h.toFixed(1) }, { text: `\n×${rate}`, fontSize: 4, color: '#9333ea' }]
          : h.toFixed(1),
        alignment: 'center',
        fontSize: 6,
        color: '#1976d2',
      };
    });
    const otDayCells = dayMeta.map(({ day }) => {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const foundCk = (emp.checkins || []).find(
        (c) => dayjs(c.checkIn).format('YYYY-MM-DD') === dateStr,
      );
      const otH = foundCk?.otRequests?.total_hours;
      const otRate = foundCk?.otRequests?.rate;
      if (!otH) return { text: '', alignment: 'center' };
      return {
        text: otRate
          ? [{ text: otH.toFixed(1) }, { text: `\n×${otRate}`, fontSize: 4, color: '#9333ea' }]
          : otH.toFixed(1),
        alignment: 'center',
        fontSize: 6,
        color: '#d32f2f',
      };
    });

    attendanceBody.push([
      { text: idx + 1, rowSpan: 2, alignment: 'center', margin: [0, 6, 0, 0] },
      { text: emp.name || '-', rowSpan: 2, alignment: 'left', margin: [0, 6, 0, 0] },
      { text: 'ปกติ', alignment: 'center' },
      ...dayCells,
      { text: fmt(emp.salary_per_day), rowSpan: 2, alignment: 'right', margin: [0, 6, 0, 0] },
      { text: (summary.displayCheck ?? 0).toFixed(1), rowSpan: 2, alignment: 'center', margin: [0, 6, 0, 0] },
      { text: (summary.netOtH ?? 0).toFixed(1), rowSpan: 2, alignment: 'center', margin: [0, 6, 0, 0] },
    ]);
    attendanceBody.push([
      {}, {},
      { text: 'OT', alignment: 'center', color: '#d32f2f' },
      ...otDayCells,
      {}, {}, {},
    ]);
  });

  // ── ตาราง 2: การคิดเงิน (ตรงกับกลุ่มรายได้/หัก/สุทธิ/ต้นทุนบริษัท ใน MergedPayrollTableV2) ──
  const WB_FONT = 6;
  const wageBody = [
    [
      { text: 'No.', style: 'tableHeader', fontSize: WB_FONT },
      { text: 'ชื่อ-สกุล', style: 'tableHeader', alignment: 'left', fontSize: WB_FONT },
      { text: 'ค่าแรง', style: 'tableHeader', alignment: 'right', fontSize: WB_FONT },
      { text: 'OT', style: 'tableHeader', alignment: 'right', fontSize: WB_FONT },
      { text: 'เบี้ยเลี้ยง', style: 'tableHeader', alignment: 'right', fontSize: WB_FONT },
      { text: 'ค่าออกสนาม', style: 'tableHeader', alignment: 'right', fontSize: WB_FONT },
      { text: 'เงินได้อื่นๆ', style: 'tableHeader', alignment: 'right', fontSize: WB_FONT },
      { text: 'ประกันสังคม', style: 'tableHeader', alignment: 'right', fontSize: WB_FONT },
      { text: 'หักภาษี', style: 'tableHeader', alignment: 'right', fontSize: WB_FONT },
      { text: 'หักประกันสังคม', style: 'tableHeader', alignment: 'right', fontSize: WB_FONT },
      { text: 'หักอื่นๆ', style: 'tableHeader', alignment: 'right', fontSize: WB_FONT },
      { text: 'สุทธิ', style: 'tableHeader', alignment: 'right', fontSize: WB_FONT },
      { text: 'ต้นทุนบริษัท', style: 'tableHeader', alignment: 'right', fontSize: WB_FONT },
    ],
  ];

  let sumSalary = 0;
  let sumOt = 0;
  let sumAllow = 0;
  let sumFieldAllow = 0;
  let sumBonus = 0;
  let sumSsoIncome = 0;
  let sumTax = 0;
  let sumSso = 0;
  let sumOther = 0;
  let sumNet = 0;
  let sumCompanyCost = 0;

  // 'work-time' (default ใน HrReportV2.js) → ใช้ actualSalaryAmt (ชม.จริง) แทน revenue.salary และไม่หักสาย
  // (สูตรเดียวกับ ActualNetCell — คอลัมน์ "ที่ต้องจ่ายพนักงาน" ที่ขึ้นจริงบนจอในโหมดนี้)
  // 'late-deduct' → ใช้ revenue.salary ตรงๆ และหักสาย (สูตรเดียวกับ NetCell — คอลัมน์ "สุทธิ")
  const isWorkTime = salaryMode === 'work-time';
  rows.forEach((emp, idx) => {
    const empId = String(emp.employee_id || '');
    const f = salaryByEmpId[empId] || {};
    const sal = isWorkTime
      ? (empSummary[empId]?.actualSalaryAmt || 0)
      : parseFloat(f?.revenue?.salary || 0);
    const ot = parseFloat(f?.revenue?.overtime || 0);
    const all = parseFloat(f?.revenue?.allowances || 0);
    const fieldAllow = parseFloat(f?.revenue?.field_allowance || 0);
    const bon = parseFloat(f?.revenue?.bonus || 0);
    const tax = parseFloat(f?.expenses?.tax || 0);
    const sso = parseFloat(f?.expenses?.sso || 0);
    const late = parseFloat(f?.expenses?.late || 0);
    const oth = parseFloat(f?.expenses?.other || 0);
    // สูตรเดียวกับ NetCell / ActualNetCell / CompanyCostCell ใน MergedPayrollTableV2 (cells.js)
    const net = sal + ot + all + fieldAllow + bon - tax - sso - oth - (isWorkTime ? 0 : late);
    const companyCost = sal + ot + all + fieldAllow + bon + sso;

    sumSalary += sal;
    sumOt += ot;
    sumAllow += all;
    sumFieldAllow += fieldAllow;
    sumBonus += bon;
    sumSsoIncome += sso;
    sumTax += tax;
    sumSso += sso;
    sumOther += oth;
    sumNet += net;
    sumCompanyCost += companyCost;

    wageBody.push([
      { text: idx + 1, alignment: 'center', fontSize: WB_FONT },
      { text: emp.name || '-', alignment: 'left', fontSize: WB_FONT },
      { text: fmt(sal), alignment: 'right', fontSize: WB_FONT },
      { text: fmt(ot), alignment: 'right', fontSize: WB_FONT },
      { text: fmt(all), alignment: 'right', fontSize: WB_FONT },
      { text: fmt(fieldAllow), alignment: 'right', fontSize: WB_FONT },
      { text: fmt(bon), alignment: 'right', fontSize: WB_FONT },
      { text: fmt(sso), alignment: 'right', fontSize: WB_FONT },
      { text: fmt(tax), alignment: 'right', fontSize: WB_FONT },
      { text: fmt(sso), alignment: 'right', fontSize: WB_FONT },
      { text: fmt(oth), alignment: 'right', fontSize: WB_FONT },
      { text: fmt(net), alignment: 'right', bold: true, fontSize: WB_FONT },
      { text: fmt(companyCost), alignment: 'right', bold: true, fontSize: WB_FONT },
    ]);
  });
  if (rows.length > 0) {
    wageBody.push([
      { text: 'รวมทั้งหมด', style: 'tableHeader', alignment: 'center', colSpan: 2, fontSize: WB_FONT },
      {},
      { text: fmt(sumSalary), style: 'tableHeader', alignment: 'right', fontSize: WB_FONT },
      { text: fmt(sumOt), style: 'tableHeader', alignment: 'right', fontSize: WB_FONT },
      { text: fmt(sumAllow), style: 'tableHeader', alignment: 'right', fontSize: WB_FONT },
      { text: fmt(sumFieldAllow), style: 'tableHeader', alignment: 'right', fontSize: WB_FONT },
      { text: fmt(sumBonus), style: 'tableHeader', alignment: 'right', fontSize: WB_FONT },
      { text: fmt(sumSsoIncome), style: 'tableHeader', alignment: 'right', fontSize: WB_FONT },
      { text: fmt(sumTax), style: 'tableHeader', alignment: 'right', fontSize: WB_FONT },
      { text: fmt(sumSso), style: 'tableHeader', alignment: 'right', fontSize: WB_FONT },
      { text: fmt(sumOther), style: 'tableHeader', alignment: 'right', fontSize: WB_FONT },
      { text: fmt(sumNet), style: 'tableHeader', alignment: 'right', fontSize: WB_FONT },
      { text: fmt(sumCompanyCost), style: 'tableHeader', alignment: 'right', fontSize: WB_FONT },
    ]);
  }

  // ── ตาราง 3: ต้นทุนตามโครงการ (สูตรเดียวกับ TimestampProjectCost.js เป๊ะ) ──
  const { rows: costRows, grandSalary } = computeProjectCostRows(projectDashboard);
  const projectBody = [
    [
      { text: 'โครงการ', style: 'tableHeader', alignment: 'left' },
      { text: 'ค่าแรง', style: 'tableHeader', alignment: 'right' },
      { text: 'OT', style: 'tableHeader', alignment: 'right' },
      { text: 'ปกส.', style: 'tableHeader', alignment: 'right' },
      { text: 'หักสาย', style: 'tableHeader', alignment: 'right' },
      { text: 'หัก ณ ที่จ่าย และอื่นๆ', style: 'tableHeader', alignment: 'right' },
      { text: 'ค่าแรงหลังหัก ปกส', style: 'tableHeader', alignment: 'right' },
      { text: 'เบี้ยเลี้ยง', style: 'tableHeader', alignment: 'right' },
      { text: 'ค่าออกสนาม', style: 'tableHeader', alignment: 'right' },
      { text: 'เงินได้อื่นๆ', style: 'tableHeader', alignment: 'right' },
      { text: 'ที่ต้องจ่าย', style: 'tableHeader', alignment: 'right' },
      { text: 'ต้นทุน', style: 'tableHeader', alignment: 'right' },
    ],
  ];
  costRows.forEach((row) => {
    const pct = grandSalary > 0 ? (((row.total_salary || 0) / grandSalary) * 100).toFixed(1) : '0.0';
    projectBody.push([
      { text: row?.project_number || '-', alignment: 'left' },
      { text: `${fmt(row?.total_salary)}\n(${pct}%)`, alignment: 'right', fontSize: 6 },
      { text: fmt(row?.total_ot), alignment: 'right' },
      { text: fmt(row?.total_sso), alignment: 'right' },
      { text: fmt(row?.total_late), alignment: 'right' },
      { text: fmt(row?.total_tax), alignment: 'right' },
      { text: fmt(row?.amount), alignment: 'right' },
      { text: fmt(row?.total_allowance), alignment: 'right' },
      { text: fmt(row?.total_field_allowance), alignment: 'right' },
      { text: fmt(row?.total_bonus), alignment: 'right' },
      { text: fmt(row?.total_payable), alignment: 'right', bold: true },
      { text: fmt(row?.total_spent), alignment: 'right', bold: true },
    ]);
  });
  if (costRows.length > 0) {
    projectBody.push([
      { text: 'รวมทั้งหมด', style: 'tableHeader', alignment: 'center' },
      { text: fmt(costRows.reduce((s, r) => s + (r.total_salary || 0), 0)), style: 'tableHeader', alignment: 'right' },
      { text: fmt(costRows.reduce((s, r) => s + (r.total_ot || 0), 0)), style: 'tableHeader', alignment: 'right' },
      { text: fmt(costRows.reduce((s, r) => s + (r.total_sso || 0), 0)), style: 'tableHeader', alignment: 'right' },
      { text: fmt(costRows.reduce((s, r) => s + (r.total_late || 0), 0)), style: 'tableHeader', alignment: 'right' },
      { text: fmt(costRows.reduce((s, r) => s + (r.total_tax || 0), 0)), style: 'tableHeader', alignment: 'right' },
      { text: fmt(costRows.reduce((s, r) => s + (r.amount || 0), 0)), style: 'tableHeader', alignment: 'right' },
      { text: fmt(costRows.reduce((s, r) => s + (r.total_allowance || 0), 0)), style: 'tableHeader', alignment: 'right' },
      { text: fmt(costRows.reduce((s, r) => s + (r.total_field_allowance || 0), 0)), style: 'tableHeader', alignment: 'right' },
      { text: fmt(costRows.reduce((s, r) => s + (r.total_bonus || 0), 0)), style: 'tableHeader', alignment: 'right' },
      { text: fmt(costRows.reduce((s, r) => s + (r.total_payable || 0), 0)), style: 'tableHeader', alignment: 'right' },
      { text: fmt(costRows.reduce((s, r) => s + (r.total_spent || 0), 0)), style: 'tableHeader', alignment: 'right' },
    ]);
  } else {
    projectBody.push([
      { text: 'ไม่มีข้อมูล', colSpan: 12, alignment: 'center', color: '#888' },
      {}, {}, {}, {}, {}, {}, {}, {}, {}, {}, {},
    ]);
  }

  // ขนาดหน้ากระดาษ ใช้ร่วมกันทั้งตาราง 2 และตาราง 3 สำหรับคำนวณความกว้างคอลัมน์แบบไดนามิก
  const PAGE_WIDTH = 841.89; // A4 landscape (pt)
  const PAGE_MARGIN_L = 10;
  const PAGE_MARGIN_R = 10;

  // คำนวณความกว้างคอลัมน์ของตาราง 3 แบบเดียวกับตาราง 2 — กันล้นออกด้านขวา + เผื่อ padding/เส้นแบ่งแนวตั้ง
  const PROJECT_SAFETY_RATIO = 0.95;
  const PROJECT_PADDING_H = 2;
  const PROJECT_V_LINE = 0.5;
  const PROJECT_BASE_WIDTHS = [55, 60, 45, 45, 45, 65, 65, 50, 55, 55, 55, 60];
  const projectBaseSum = PROJECT_BASE_WIDTHS.reduce((s, w) => s + w, 0);
  const projectColCount = PROJECT_BASE_WIDTHS.length;
  const projectPaddingOverhead = 2 * (projectColCount - 1) * PROJECT_PADDING_H + (projectColCount + 1) * PROJECT_V_LINE;
  const projectAvailable = (PAGE_WIDTH - PAGE_MARGIN_L - PAGE_MARGIN_R) * PROJECT_SAFETY_RATIO - projectPaddingOverhead;
  const projectScale = projectAvailable / projectBaseSum;
  const projectWidths = PROJECT_BASE_WIDTHS.map((w) => Math.floor(w * projectScale));
  const projectLayout = {
    hLineWidth(i, node) {
      if (i === 0 || i === node.table.body.length) return 0;
      return i === node.table.headerRows ? 1.5 : 0.5;
    },
    vLineWidth() { return PROJECT_V_LINE; },
    hLineColor(i) { return i === 1 ? '#333' : '#aaa'; },
    vLineColor() { return '#bbb'; },
    paddingLeft(i) { return i === 0 ? 0 : PROJECT_PADDING_H; },
    paddingRight(i, node) { return i === node.table.widths.length - 1 ? 0 : PROJECT_PADDING_H; },
    paddingTop() { return 1; },
    paddingBottom() { return 1; },
  };

  // คำนวณความกว้างคอลัมน์ของตาราง 2 (การคิดเงิน) แบบไดนามิก ให้ผลรวมทั้งตารางพอดีกับหน้ากระดาษเสมอ
  // กันล้นออกด้านขวา — เดิมกำหนดความกว้างตายตัวทีละคอลัมน์ รวมกันเกินความกว้างหน้ากระดาษที่ใช้ได้จริง
  // — เผื่อ safety margin ไว้ 8% (ไม่จองเต็ม 100% ของพื้นที่ที่ใช้ได้ กันคลาดเคลื่อนจากการวัด/ปัดเศษ)
  // หมายเหตุ: layout 'lightHorizontalLines' ของ pdfmake บวก padding 8pt เข้าไปทั้งซ้ายขวาของทุกคอลัมน์
  // (นอกเหนือจาก width ที่กำหนด) — 14 คอลัมน์ = padding รวม 208pt ซึ่งเกินพอที่จะดันคอลัมน์ท้ายๆ
  // ล้นออกนอกหน้ากระดาษ จึงเปลี่ยนมาใช้ custom layout padding แคบ (2pt) แล้วหักส่วนนี้ออกจากพื้นที่ที่คำนวณ
  const WAGE_SAFETY_RATIO = 0.95;
  const WAGE_PADDING_H = 2;
  const WAGE_V_LINE = 0.5;
  const WAGE_BASE_WIDTHS = [20, 70, 50, 40, 45, 50, 50, 50, 45, 55, 40, 50, 55];
  const wageBaseSum = WAGE_BASE_WIDTHS.reduce((s, w) => s + w, 0);
  const wageColCount = WAGE_BASE_WIDTHS.length;
  // overhead = padding ซ้าย/ขวาที่คอลัมน์ภายใน (ไม่นับขอบนอกสุด) + เส้นแบ่งแนวตั้งทุกช่อง (n+1 เส้น)
  const wagePaddingOverhead = 2 * (wageColCount - 1) * WAGE_PADDING_H + (wageColCount + 1) * WAGE_V_LINE;
  const wageAvailable = (PAGE_WIDTH - PAGE_MARGIN_L - PAGE_MARGIN_R) * WAGE_SAFETY_RATIO - wagePaddingOverhead;
  const wageScale = wageAvailable / wageBaseSum;
  const wageWidths = WAGE_BASE_WIDTHS.map((w) => Math.floor(w * wageScale));
  const wageLayout = {
    hLineWidth(i, node) {
      if (i === 0 || i === node.table.body.length) return 0;
      return i === node.table.headerRows ? 1.5 : 0.5;
    },
    vLineWidth() { return WAGE_V_LINE; },
    hLineColor(i) { return i === 1 ? '#333' : '#aaa'; },
    vLineColor() { return '#bbb'; },
    paddingLeft(i) { return i === 0 ? 0 : WAGE_PADDING_H; },
    paddingRight(i, node) { return i === node.table.widths.length - 1 ? 0 : WAGE_PADDING_H; },
    paddingTop() { return 1; },
    paddingBottom() { return 1; },
  };

  // ใช้ unbreakable: true ให้หัวข้อ + ตารางของ section ไปขึ้นหน้าใหม่พร้อมกันเป็นก้อนเดียว ถ้าที่เหลือใน
  // หน้าปัจจุบันไม่พอวางทั้งตาราง (กันปัญหาหัวข้อค้างท้ายหน้านี้ แต่ตารางไปขึ้นหน้าถัดไป)
  // จำกัดเฉพาะ section ที่แถวไม่เยอะเกินไป — pdfmake ไม่รองรับ unbreakable block ที่สูงเกิน 1 หน้ากระดาษ
  // (จะตัดแถวส่วนเกินหายไปเงียบๆ) จึงปล่อยให้ section ที่แถวเยอะ (เช่นบริษัทพนักงานเยอะ) แตกหน้าตามปกติแทน
  const SAFE_UNBREAKABLE_ROWS = 35;

  const docDefinition = {
    pageSize: 'A4',
    pageOrientation: 'landscape',
    pageMargins: [PAGE_MARGIN_L, 80, PAGE_MARGIN_R, 40],
    defaultStyle: {
      font: 'Sarabun',
      fontSize: 7,
    },
    info: {
      title: 'HR Payroll Full Report',
      author: 'MNS System',
      subject: `Payroll Report ${dayjs(dateStart).format('MMMM YYYY')}`,
    },
    header(currentPage, pageCount, pageSize) {
      return [
        {
          columns: [
            { text: info?.name, style: 'header', alignment: 'left', margin: [20, 10, 0, 0] },
            { text: 'รายงานเงินเดือนฉบับเต็ม', style: 'header', alignment: 'center', margin: [0, 10, 0, 0] },
            { text: `วันที่พิมพ์: ${dayjs().format('DD/MM/YYYY HH:mm')}`, alignment: 'right', fontSize: 10, margin: [0, 10, 20, 0] },
          ],
        },
        { text: `เดือน ${dayjs(dateStart).format('MMMM YYYY')}`, alignment: 'center', fontSize: 12, margin: [0, 2, 0, 10] },
        { canvas: [{ type: 'line', x1: 20, y1: 0, x2: pageSize.width - 20, y2: 0, lineWidth: 1, lineColor: '#bbb' }], margin: [0, 0, 0, 10] },
      ];
    },
    content: [
      { text: '1. ข้อมูลการลงเวลา', style: 'sectionHeader', margin: [0, 0, 0, 6] },
      {
        table: {
          headerRows: 1,
          widths: [18, '*', 25, ...days.map(() => 12), 35, 30, 30],
          body: attendanceBody,
        },
        layout: {
          hLineWidth(i, node) {
            if (i === 0 || i === node.table.body.length) return 1.5;
            return 0.5;
          },
          vLineWidth() { return 0.7; },
          vLineColor() { return '#bbb'; },
          paddingLeft() { return 1; },
          paddingRight() { return 1; },
          paddingTop() { return 1; },
          paddingBottom() { return 1; },
        },
        margin: [0, 0, 0, 14],
      },
      {
        ...(wageBody.length <= SAFE_UNBREAKABLE_ROWS ? { unbreakable: true } : {}),
        stack: [
          { text: '2. การคิดเงิน', style: 'sectionHeader', margin: [0, 8, 0, 6] },
          {
            table: {
              headerRows: 1,
              widths: wageWidths,
              body: wageBody,
            },
            layout: wageLayout,
            margin: [0, 0, 0, 14],
          },
        ],
      },
      {
        ...(projectBody.length <= SAFE_UNBREAKABLE_ROWS ? { unbreakable: true } : {}),
        stack: [
          { text: '3. ต้นทุนตามโครงการ', style: 'sectionHeader', margin: [0, 8, 0, 6] },
          {
            table: {
              headerRows: 1,
              widths: projectWidths,
              body: projectBody,
            },
            layout: projectLayout,
          },
        ],
      },
    ],
    footer(currentPage, pageCount, pageSize) {
      return [
        { canvas: [{ type: 'line', x1: 20, y1: 0, x2: pageSize.width - 20, y2: 0, lineWidth: 1, lineColor: '#bbb' }], margin: [0, 0, 0, 6] },
        {
          columns: [
            { text: info?.name_eng, alignment: 'left', fontSize: 10, margin: [20, 2, 0, 0] },
            { text: `หน้าที่ ${currentPage} / ${pageCount}`, alignment: 'right', fontSize: 10, margin: [0, 2, 20, 0] },
          ],
        },
      ];
    },
    styles: {
      header: { fontSize: 14, bold: true },
      sectionHeader: { fontSize: 12, bold: true, color: '#1e293b' },
      tableHeader: { bold: true, fillColor: '#CCCCCC', alignment: 'center' },
    },
  };

  pdfMake.createPdf(docDefinition).open();
};

export default hrReportFullPDF;
